import EmptyState from '@/components/EmptyState';
import { env } from '@/env';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { countryToLocale } from '@/utils/countryToLocale';
import type { TableState } from '@restaurant/api-client';
import { Block, Button, Navbar, NavRight, Page, Preloader } from 'framework7-react';
import QRCode from 'qrcode';
import { useEffect, useMemo, useState } from 'react';
import { useTableStates } from '../hooks/useTableOrdering';

// The QR encodes the public guest URL `<storefront>/<locale>/t/<qrToken>`. The
// locale segment is baked in from the restaurant's country so a scan opens in
// the restaurant's own language regardless of the scanner's browser language
// (the storefront's `/{locale}/t/…` route accepts an explicit locale). The
// storefront origin is environment config (Tailnet / prod domain differ);
// falls back to the storefront dev port for local dev.
const STOREFRONT_BASE = env.storefrontUrl
  ? env.storefrontUrl.replace(/\/$/, '')
  : 'http://localhost:3140';

/**
 * Printable QR sheet - one card per table with its scannable code and the
 * human-readable URL underneath. `window.print()` plus print CSS hides the
 * app chrome so it comes out as a clean cut-and-place sheet. Tables without a
 * QR token are listed separately so nothing is silently missing.
 */
export default function TableQrSheetPage() {
  const tAll = useT();
  const t = tAll.tableOrdering.qrSheet;
  const tGrid = tAll.tableOrdering.tableGrid;
  const { data, isLoading, error } = useTableStates();
  const [codes, setCodes] = useState<Record<string, string>>({});
  const locale = countryToLocale(useRestaurantStore((s) => s.restaurantConfig?.country));

  const withToken = useMemo(
    () => (data ?? []).filter((tb): tb is TableState & { qrToken: string } => !!tb.qrToken),
    [data],
  );
  const withoutToken = (data ?? []).filter((tb) => !tb.qrToken);

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      withToken.map(async (tb) => {
        const url = `${STOREFRONT_BASE}/${locale}/t/${tb.qrToken}`;
        const dataUrl = await QRCode.toDataURL(url, {
          margin: 1,
          width: 320,
          errorCorrectionLevel: 'M',
        });
        return [tb.tableId, dataUrl] as const;
      }),
    ).then((pairs) => {
      if (!cancelled) setCodes(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [withToken, locale]);

  return (
    <Page name="table-qr-sheet">
      <Navbar title={t.title} backLink={tAll.back}>
        <NavRight>
          <Button onClick={() => window.print()}>{t.print}</Button>
        </NavRight>
      </Navbar>

      {isLoading ? (
        <Block style={{ textAlign: 'center', paddingTop: 48 }}>
          <Preloader />
        </Block>
      ) : error ? (
        <EmptyState
          icon="exclamationmark_triangle"
          title={tGrid.couldntLoadTables}
          description={t.tryAgain}
        />
      ) : withToken.length === 0 ? (
        <EmptyState icon="qrcode" title={t.noQR} description={t.noQRDesc} />
      ) : (
        <>
          <Block className="qr-print-hint">{t.hint}</Block>
          <div className="qr-sheet-grid">
            {withToken.map((tb) => (
              <div className="qr-card" key={tb.tableId}>
                {codes[tb.tableId] ? (
                  <img src={codes[tb.tableId]} alt={t.qrAlt(tb.label)} className="qr-img" />
                ) : (
                  <div className="qr-img qr-img--loading">
                    <Preloader />
                  </div>
                )}
                <div className="qr-label">{tb.label}</div>
                {tb.zoneLabel && <div className="qr-zone">{tb.zoneLabel}</div>}
                <div className="qr-url">
                  {STOREFRONT_BASE.replace(/^https?:\/\//, '')}/{locale}/t/{tb.qrToken}
                </div>
              </div>
            ))}
          </div>

          {withoutToken.length > 0 && (
            <Block strongIos strongMd outlineIos className="qr-missing">
              <b>{t.noQRYet}</b> {withoutToken.map((tb) => tb.label).join(', ')}. {t.reseeding}
            </Block>
          )}
        </>
      )}

      <style>{`
        .qr-sheet-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 14px;
          padding: 12px 16px 28px;
        }
        .qr-card {
          border: 1px solid rgba(0,0,0,0.18);
          border-radius: 14px;
          padding: 18px 14px;
          text-align: center;
          background: #fff;
          break-inside: avoid;
        }
        .qr-img { width: 100%; max-width: 200px; aspect-ratio: 1; margin: 0 auto; display: block; }
        .qr-img--loading { display: flex; align-items: center; justify-content: center; }
        .qr-label { font-size: 19px; font-weight: 700; margin-top: 12px; color: #000; }
        .qr-zone { font-size: 13px; color: #666; }
        .qr-url { font-size: 11px; color: #888; margin-top: 8px; word-break: break-all; }
        .qr-missing { color: var(--f7-text-color); }
        @media print {
          .navbar, .toolbar, .tabbar, .qr-print-hint, .qr-missing { display: none !important; }
          .page, .page-content { background: #fff !important; }
          .qr-sheet-grid { grid-template-columns: repeat(3, 1fr); gap: 10px; }
          .qr-card { border-color: #000; }
        }
      `}</style>
    </Page>
  );
}
